import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BellRing } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { requestNotificationPermission } from "@/lib/firebase";

const STORAGE_KEY = "reallyfan_push_banner_dismissed_at";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 90 * 1000; // 1.5 min in app

export const PushDiscoveryBanner = () => {
  const { user, api } = useAuth();
  const [visible, setVisible] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (typeof Notification === "undefined") return;
    // Already granted or denied -> never show
    if (Notification.permission !== "default") return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const days = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user]);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      const token = await requestNotificationPermission();
      if (token) {
        await api.post("/push/subscribe", {
          endpoint: token,
          keys: { fcm: true },
        });
        toast.success("Notifications activees !");
      } else {
        toast.error("Permission refusee");
      }
    } catch {
      toast.error("Erreur lors de l'activation");
    }
    setVisible(false);
    setRequesting(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed top-3 left-3 right-3 z-50 bg-gradient-to-r from-[#FF6B35] to-[#7000FF] rounded-3xl p-4 shadow-2xl flex items-center gap-3 md:left-auto md:right-4 md:max-w-sm"
          data-testid="push-discovery-banner"
        >
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-white">Active les notifications</p>
            <p className="text-[11px] text-white/80">Ne rate aucune enchere ni nouveau drop</p>
          </div>
          <button
            onClick={handleEnable}
            disabled={requesting}
            className="bg-white text-[#FF6B35] font-bold px-3 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 shrink-0"
            data-testid="push-enable-btn"
          >
            {requesting ? "..." : "Activer"}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-white/80 hover:text-white transition-colors shrink-0"
            data-testid="push-dismiss-btn"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
